import config from '@/config';
import { cn } from '@/lib/utils';
import {
  convertCoordsToGoogleMapsFormat,
  convertCoordToGoogleMapsFormat,
  parseLatLng,
} from '@/utils/map';
import centroid from '@turf/centroid';
import {
  AdvancedMarker,
  ControlPosition,
  Map,
  MapControl,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { Fragment, useEffect, useRef, useState } from 'react';
import { GeoJSONPoint } from 'wellknown';
import { Circle } from '../map/circle';
import { Polygon } from '../map/polygon';
import { Button } from './Button';
import { Input } from './Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './Select';

// TODO: This component need more work
export type GMapProps = {
  displayItems?: {
    key: string;
    geometry: GeoJSON.Geometry;
  }[];
  center?: google.maps.LatLng | google.maps.LatLngLiteral;
  className?: string;
  draw?: boolean;
  editingGeometry?: GeoJSON.Geometry;
  centerCircleRadius?: number;
  onEditGeometryChange?: (point: GeoJSONPoint) => void;
  onCenterChange?: (center: google.maps.LatLng) => void;
  onCenterCircleRadiusChange?: (radius: number) => void;
};

function GMap({
  displayItems = [],
  centerCircleRadius,
  className,
  center,
  draw,
  editingGeometry,
  onEditGeometryChange,
  onCenterChange,
  onCenterCircleRadiusChange,
}: GMapProps) {
  const map = useMap();
  const [showCenterCircle, setShowCenterCircle] = useState<boolean>();
  const inputRef = useRef<HTMLInputElement>(null);
  const placesLib = useMapsLibrary('places');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const DEFAULT_CENTER: google.maps.LatLngLiteral = {
    lng: 144.95045212513423,
    lat: -37.83359613440826,
  };

  useEffect(() => {
    if (map && center) {
      map.panTo(center);
    }
  }, [map, center]);

  useEffect(() => {
    if (!editingGeometry || !map) return;
    const center = convertCoordToGoogleMapsFormat(
      centroid(editingGeometry).geometry.coordinates,
    );
    map.panTo(center);
    setCurrentMarker(center);
  }, [editingGeometry, map]);

  const [currentMarker, setCurrentMarker] = useState<
    google.maps.LatLng | google.maps.LatLngLiteral
  >();

  useEffect(() => {
    if (!placesLib || !inputRef.current || !draw || !map) return;

    const input = inputRef.current;

    const updatePoint = (location: google.maps.LatLng) => {
      const newPoint: GeoJSONPoint = {
        type: 'Point',
        coordinates: [location.lng(), location.lat()],
      };
      if (onEditGeometryChange) {
        onEditGeometryChange(newPoint);
      }
    };

    const handleManualInput = () => {
      const val = input.value.trim();
      const coords = parseLatLng(val);
      if (coords) {
        const position = new google.maps.LatLng(coords.lat, coords.lng);
        setCurrentMarker(position);
        map.panTo(position);
        map.setZoom(15);
        updatePoint(position);
      }
    };

    input.addEventListener('change', handleManualInput);

    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      fields: ['geometry', 'name'],
    });

    const placeChangedListener = autocomplete.addListener(
      'place_changed',
      () => {
        const place = autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) return;

        const location = place.geometry.location;
        map.panTo(location);
        map.setZoom(15);
        setCurrentMarker(location);
        updatePoint(location);
      },
    );

    // Google Maps sets cursor:grab on the body/map container, which the pac-container
    // inherits. Radix UI modals also intercept pointerdown outside the dialog subtree,
    // swallowing clicks before autocomplete can react. Fix both by:
    // 1. Injecting CSS to override cursor and pointer-events on the pac-container.
    // 2. Stopping propagation on pointerdown so Radix doesn't treat it as "outside click".
    const style = document.createElement('style');
    style.textContent = `
      .pac-container {
        pointer-events: auto !important;
        z-index: 9999 !important;
        cursor: default !important;
      }
      .pac-item, .pac-icon, .pac-item-query, .pac-matched {
        pointer-events: auto !important;
        cursor: pointer !important;
      }
    `;
    document.head.appendChild(style);

    const stopPropagation = (e: Event) => e.stopPropagation();
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (
            node instanceof HTMLElement &&
            node.classList.contains('pac-container')
          ) {
            node.addEventListener('mousedown', stopPropagation);
            node.addEventListener('pointerdown', stopPropagation);
          }
        });
      }
    });
    observer.observe(document.body, { childList: true });

    return () => {
      input.removeEventListener('change', handleManualInput);
      google.maps.event.removeListener(placeChangedListener);
      observer.disconnect();
      document.head.removeChild(style);
      document.querySelectorAll('.pac-container').forEach((el) => {
        el.removeEventListener('mousedown', stopPropagation);
        el.removeEventListener('pointerdown', stopPropagation);
      });
    };
  }, [placesLib, map, draw, onEditGeometryChange]);

  const updateDraggingLocation = (e: google.maps.MapMouseEvent) => {
    const newPoint: GeoJSONPoint = {
      type: 'Point',
      coordinates: [e.latLng!.lng(), e.latLng!.lat()],
    };
    if (onEditGeometryChange) {
      onEditGeometryChange(newPoint);
    }
  };

  const handleCenterChange = () => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      const center = map?.getCenter();
      if (onCenterChange && center) {
        // getCenter() may return an unwrapped longitude (outside ±180) after
        // panning across the antimeridian; the LatLng constructor wraps it.
        onCenterChange(new google.maps.LatLng(center.lat(), center.lng()));
      }
    }, 250);
  };

  return (
    <>
      <Map
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={15}
        mapId={config.googleMap.mapId}
        className={cn('h-[400px] w-full', className)}
        zoomControl={true}
        onCenterChanged={handleCenterChange}
        scrollwheel={true}
      >
        {currentMarker && (
          <AdvancedMarker
            clickable={true}
            draggable={!!draw}
            position={currentMarker}
            onDragEnd={updateDraggingLocation}
          >
            <img src="/location-green.png" alt="Device location" />
          </AdvancedMarker>
        )}

        {displayItems.map((item, _index) => {
          if (item.geometry?.type === 'Polygon') {
            const polygon = convertCoordsToGoogleMapsFormat(
              item.geometry.coordinates[0],
            );
            const polygonCenter = convertCoordToGoogleMapsFormat(
              centroid(item.geometry).geometry.coordinates,
            );
            return (
              <Fragment key={`g_${item.key}`}>
                <Polygon
                  key={`p_${item.key}`}
                  strokeColor={item.key === 'site' ? '#22c55e' : '#3b82f6'}
                  paths={polygon}
                ></Polygon>
                <AdvancedMarker
                  clickable={true}
                  key={`m_${item.key}`}
                  position={polygonCenter}
                  title={item.key}
                  zIndex={1}
                >
                  {item.key === 'site' ? (
                    <img src="/location-green.png" alt="Device location" />
                  ) : (
                    <img src="/location-blue.png" alt="Zone" />
                  )}
                </AdvancedMarker>
              </Fragment>
            );
          }
          if (item.geometry?.type === 'Point') {
            const point = convertCoordToGoogleMapsFormat(
              item.geometry.coordinates,
            );
            return (
              <AdvancedMarker
                key={`m_${item.key}`}
                position={point}
                title={item.key}
                clickable={true}
                zIndex={2}
              >
                {item.key === 'site' ? (
                  <img src="/location-green.png" alt="Device location" />
                ) : (
                  <img src="/location-blue.png" alt="Zone" />
                )}
              </AdvancedMarker>
            );
          }
          return null;
        })}

        {showCenterCircle && (
          <Circle
            zIndex={5}
            center={map?.getCenter()}
            radius={centerCircleRadius}
            fillColor="#3b82f6"
            fillOpacity={0.1}
            strokeColor="#3b82f6"
            strokeWeight={1.5}
          />
        )}

        {draw && (
          <MapControl position={ControlPosition.TOP_LEFT}>
            <Input
              type="text"
              id="location"
              ref={inputRef}
              className="mt-3 w-[200px] bg-white lg:w-[200px] xl:w-[300px] dark:bg-white dark:text-black"
              placeholder="Enter lat lng or address..."
            />
          </MapControl>
        )}

        {centerCircleRadius && (
          <MapControl position={ControlPosition.BOTTOM_CENTER}>
            <div className="mb-4 flex gap-1">
              <Button
                className="bg-white dark:bg-white dark:text-black dark:hover:bg-gray-200"
                variant={'outline'}
                onClick={() => {
                  setShowCenterCircle(!showCenterCircle);
                }}
              >
                {showCenterCircle ? 'Hide' : 'Show'} Zone Fetch Radius
              </Button>
              <Select
                value={centerCircleRadius.toString()}
                onValueChange={(val) => {
                  if (onCenterCircleRadiusChange) {
                    onCenterCircleRadiusChange(parseInt(val));
                  }
                }}
              >
                <SelectTrigger className="w-[100px] bg-white dark:bg-white dark:text-black dark:hover:bg-gray-200">
                  <SelectValue placeholder="Radius" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 m</SelectItem>
                  <SelectItem value="50">50 m</SelectItem>
                  <SelectItem value="100">100 m</SelectItem>
                  <SelectItem value="200">200 m</SelectItem>
                  <SelectItem value="500">500 m</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </MapControl>
        )}
      </Map>
    </>
  );
}

export default GMap;
